import { HTMLAttributes } from 'react';
import { cn } from '@lib/utils/cn';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';

interface AlertProps extends HTMLAttributes<HTMLDivElement> {
  variant?: 'success' | 'warning' | 'danger' | 'info';
  title?: string;
  onClose?: () => void;
  'data-testid'?: string;
}

const icons = {
  success: CheckCircleIcon,
  warning: ExclamationTriangleIcon,
  danger: XCircleIcon,
  info: InformationCircleIcon,
};

const variants = {
  success: 'bg-success-50 dark:bg-success-950 border-success-200 dark:border-success-800 text-success-800 dark:text-success-200',
  warning: 'bg-warning-50 dark:bg-warning-950 border-warning-200 dark:border-warning-800 text-warning-800 dark:text-warning-200',
  danger: 'bg-danger-50 dark:bg-danger-950 border-danger-200 dark:border-danger-800 text-danger-800 dark:text-danger-200',
  info: 'bg-primary-50 dark:bg-primary-950 border-primary-200 dark:border-primary-800 text-primary-800 dark:text-primary-200',
};

export function Alert({
  variant = 'info',
  title,
  children,
  onClose,
  className,
  'data-testid': testId,
  ...props
}: AlertProps) {
  const Icon = icons[variant];

  return (
    <div
      data-testid={testId}
      className={cn(
        'flex items-start gap-3 p-4 rounded-lg border',
        variants[variant],
        className
      )}
      {...props}
    >
      <Icon className="h-5 w-5 mt-0.5 flex-shrink-0" />
      <div className="flex-1">
        {title && <h4 className="font-semibold mb-1">{title}</h4>}
        {children && <div className="text-sm">{children}</div>}
      </div>
      {onClose && (
        <button
          onClick={onClose}
          className="text-current hover:opacity-70 transition-opacity"
        >
          <XCircleIcon className="h-5 w-5" />
        </button>
      )}
    </div>
  );
}
